import { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import {
  ArrowLeft,
  Loader2,
  Save,
  Globe,
  FileText,
  Edit3,
  Layers,
} from "lucide-react";
import { useTenant } from "../contexts/TenantContext";
import { colors, fonts } from "../styles/theme";
import { fetchContentBlocks, saveContentBlock, publishStatic, fmtDate } from "../services/api";

const GrapesEditor = lazy(() => import("../components/GrapesEditor"));

const STATUS_BADGES = {
  draft: { label: "Draft", color: "#94a3b8", bg: "rgba(148,163,184,0.12)" },
  published: { label: "Published", color: "#10b981", bg: "rgba(16,185,129,0.10)" },
  none: { label: "Not Created", color: "#64748b", bg: "rgba(100,116,139,0.10)" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_BADGES[status] || STATUS_BADGES.none;
  return (
    <span
      style={{
        padding: "3px 10px",
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 600,
        color: cfg.color,
        background: cfg.bg,
      }}
    >
      {cfg.label}
    </span>
  );
}

export default function ContentBlocks() {
  const tenant = useTenant();
  const blockDefs = tenant.contentBlocks || [];
  const [blocks, setBlocks] = useState({}); // blockId -> block data from API
  const [editing, setEditing] = useState(null); // blockId being edited
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [loading, setLoading] = useState(true);
  const gjsDataRef = useRef({});

  // Group blockDefs by page
  const grouped = {};
  for (const def of blockDefs) {
    if (!grouped[def.page]) grouped[def.page] = [];
    grouped[def.page].push(def);
  }

  // Fetch all existing blocks on mount
  const loadBlocks = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchContentBlocks();
      const map = {};
      for (const b of list) map[b.blockId] = b;
      setBlocks(map);
    } catch (err) {
      console.error("Failed to load content blocks:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (blockDefs.length > 0) loadBlocks();
    else setLoading(false);
  }, [blockDefs.length, loadBlocks]);

  const handleGrapesUpdate = useCallback((data) => {
    gjsDataRef.current = data;
  }, []);

  const doSave = async (status) => {
    if (!editing) return;
    const isSave = status === "draft";
    if (isSave) setSaving(true);
    else setPublishing(true);

    try {
      const gjs = gjsDataRef.current;
      const data = {
        blockId: editing,
        bodyHtml: gjs.bodyHtml || "",
        gjsProjectData: gjs.gjsProjectData || null,
        gjsHtml: gjs.gjsHtml || "",
        gjsCss: gjs.gjsCss || "",
        status,
      };
      await saveContentBlock(data);

      if (status === "published") {
        try {
          await publishStatic();
        } catch (err) {
          console.warn("Static publish failed (site will use API fallback):", err.message);
        }
      }

      setEditing(null);
      loadBlocks();
    } catch (err) {
      alert("Save failed: " + err.message);
    } finally {
      setSaving(false);
      setPublishing(false);
    }
  };

  // No content blocks configured for this tenant
  if (blockDefs.length === 0) {
    return (
      <div style={{ padding: 32, maxWidth: 1100, margin: "0 auto" }}>
        <h1
          style={{
            fontSize: 24,
            fontWeight: 700,
            color: colors.text,
            fontFamily: fonts.heading,
            marginBottom: 4,
          }}
        >
          Content Blocks
        </h1>
        <div
          style={{
            textAlign: "center",
            padding: 60,
            color: colors.textSecondary,
          }}
        >
          <Layers size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
          <p style={{ fontSize: 15 }}>Content blocks are not configured for this tenant</p>
        </div>
      </div>
    );
  }

  // ── Edit view ──
  if (editing) {
    const def = blockDefs.find((d) => d.id === editing);
    const existing = blocks[editing];

    // Initialize gjsDataRef with existing data
    if (existing && !gjsDataRef.current?.gjsProjectData) {
      gjsDataRef.current = {
        gjsProjectData: existing.gjsProjectData || null,
        gjsHtml: existing.gjsHtml || "",
        gjsCss: existing.gjsCss || "",
        bodyHtml: existing.bodyHtml || "",
      };
    }

    const editorPage = existing || {};

    return (
      <div style={{ padding: 32, maxWidth: 1100, margin: "0 auto", paddingBottom: 60 }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 24,
          }}
        >
          <button
            onClick={() => {
              gjsDataRef.current = {};
              setEditing(null);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
              background: colors.bgCard,
              color: colors.textSecondary,
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            <ArrowLeft size={16} /> Back
          </button>
          <div style={{ flex: 1 }}>
            <h1
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: colors.text,
                fontFamily: fonts.heading,
                margin: 0,
              }}
            >
              {def?.label || editing}
            </h1>
            <p style={{ fontSize: 12, color: colors.textSecondary, margin: "2px 0 0" }}>
              {def?.page} &middot; {def?.id}
            </p>
          </div>
          {existing && <StatusBadge status={existing.status} />}
        </div>

        {/* Editor */}
        <Suspense
          fallback={
            <div style={{ textAlign: "center", padding: 60, color: colors.textSecondary }}>
              <Loader2 size={24} className="spin" />            </div>
          }
        >
          <GrapesEditor
            page={editorPage}
            onUpdate={handleGrapesUpdate}
            isLanding={false}
          />
        </Suspense>

        {/* Action buttons */}
        <div
          style={{
            display: "flex",
            gap: 10,
            marginTop: 20,
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={() => doSave("draft")}
            disabled={saving || publishing}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "10px 20px",
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
              background: colors.bgCard,
              color: colors.text,
              fontSize: 14,
              fontWeight: 600,
              cursor: saving || publishing ? "not-allowed" : "pointer",
              opacity: saving || publishing ? 0.6 : 1,
            }}
          >
            {saving ? (
              <Loader2 size={16} className="spin" />
            ) : (
              <Save size={16} />
            )}
            Save Draft
          </button>
          <button
            onClick={() => doSave("published")}
            disabled={saving || publishing}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "10px 20px",
              borderRadius: 8,
              border: "none",
              background: saving || publishing ? "#1e40af" : "#2563eb",
              color: "#fff",
              fontSize: 14,
              fontWeight: 600,
              cursor: saving || publishing ? "not-allowed" : "pointer",
              opacity: saving || publishing ? 0.6 : 1,
            }}
          >
            {publishing ? (
              <Loader2 size={16} className="spin" />
            ) : (
              <Globe size={16} />
            )}
            Publish
          </button>
        </div>
      </div>
    );
  }

  // ── List view ──
  const totalBlocks = blockDefs.length;
  const publishedCount = blockDefs.filter((d) => blocks[d.id]?.status === "published").length;
  const draftCount = blockDefs.filter((d) => blocks[d.id]?.status === "draft").length;
  const notCreated = totalBlocks - publishedCount - draftCount;

  const statCards = [
    { label: "Total", value: totalBlocks, color: colors.accent },
    { label: "Published", value: publishedCount, color: "#10b981" },
    { label: "Drafts", value: draftCount, color: "#94a3b8" },
    { label: "Not Created", value: notCreated, color: "#64748b" },
  ];

  return (
    <div style={{ padding: 32, maxWidth: 1100, margin: "0 auto", paddingBottom: 60 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 28,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 24,
              fontWeight: 700,
              color: colors.text,
              fontFamily: fonts.heading,
              marginBottom: 4,
            }}
          >
            Content Blocks
          </h1>
          <p style={{ fontSize: 13, color: colors.textSecondary }}>
            Manage reusable content blocks for your website
          </p>
        </div>
      </div>

      {/* Stat cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}
      >
        {statCards.map((s) => (
          <div
            key={s.label}
            style={{
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: 12,
              padding: "16px 20px",
            }}
          >
            <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 4 }}>
              {s.label}
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Loading */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: colors.textSecondary }}>
          <Loader2 size={24} className="spin" />        </div>
      ) : (
        /* Blocks grouped by page */
        Object.entries(grouped).map(([pageName, defs]) => (
          <div key={pageName} style={{ marginBottom: 28 }}>
            {/* Page group header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 12,
              }}
            >
              <FileText size={16} color={colors.textSecondary} />
              <h2
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: colors.text,
                  fontFamily: fonts.heading,
                  margin: 0,
                  textTransform: "capitalize",
                }}
              >
                {pageName}
              </h2>
              <span
                style={{
                  fontSize: 11,
                  color: colors.textSecondary,
                  fontWeight: 500,
                }}
              >
                ({defs.length} block{defs.length !== 1 ? "s" : ""})
              </span>
            </div>

            {/* Block cards */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                gap: 12,
              }}
            >
              {defs.map((def) => {
                const existing = blocks[def.id];
                const status = existing?.status || "none";

                return (
                  <div
                    key={def.id}
                    style={{
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: 12,
                      padding: "18px 20px",
                      transition: "border-color 0.15s, box-shadow 0.15s",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = colors.accent;
                      e.currentTarget.style.boxShadow = `0 0 0 1px ${colors.accent}20`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = colors.border;
                      e.currentTarget.style.boxShadow = "none";
                    }}
                    onClick={() => {
                      gjsDataRef.current = {};
                      setEditing(def.id);
                    }}
                  >
                    {/* Card header */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: 10,
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: colors.text,
                            marginBottom: 2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {def.label}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: colors.textSecondary,
                            fontFamily: fonts.mono,
                          }}
                        >
                          {def.id}
                        </div>
                      </div>
                      <StatusBadge status={status} />
                    </div>

                    {/* Card footer */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ fontSize: 12, color: colors.textSecondary }}>
                        {existing?.updatedAt
                          ? `Updated ${fmtDate(existing.updatedAt)}`
                          : "Not yet created"}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          gjsDataRef.current = {};
                          setEditing(def.id);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          padding: "5px 12px",
                          borderRadius: 6,
                          border: `1px solid ${colors.border}`,
                          background: "transparent",
                          color: colors.textSecondary,
                          fontSize: 12,
                          fontWeight: 500,
                          cursor: "pointer",
                          transition: "all 0.15s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = colors.accent;
                          e.currentTarget.style.color = colors.accent;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = colors.border;
                          e.currentTarget.style.color = colors.textSecondary;
                        }}
                      >
                        <Edit3 size={12} /> Edit
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
